/**
 * Schémas Zod des widgets du Tableau de bord enrichi (Lot H.3) — partagés API ⇄ ERP.
 *
 * Sorties des 5 widgets manquants du dashboard, TOUS scopés `domaine_id` côté use case :
 *   - Ventes du mois        : total HT (centimes) + série pour Sparkline.
 *   - Stock parcellaire      : répartition de l'encépagement (surface en production/cépage).
 *   - Activité récente       : 5 dernières opérations (source `audit_log`).
 *   - Rendement par parcelle : réel kg/ha vs disponible/butoir (3 zones, cadrage § 1).
 *   - Performance commerciale par cuvée : CA HT / prix moyen / volume — JAMAIS de marge.
 *
 * GARDE-FOU MÉTIER (non négociable, cadrage § 2) : la MARGE BRUTE est INCALCULABLE au
 * MVP (aucune donnée de coût de production) → on ne l'affiche JAMAIS. On expose ce qui
 * est 100 % RÉEL. Aucun chiffre inventé, aucune alerte de non-conformité abusive.
 *
 * Conventions transverses : montants en CENTIMES entiers, surfaces en HECTARES, poids
 * en KILOGRAMMES, vocabulaire FR. Les seuils de rendement (disponible / butoir) sont LUS
 * depuis `campagnes` (jamais hardcodés).
 */

import { z } from "zod";

import { cuveeStyleSchema } from "./cuvee-commerciale.js";
import { idSchema } from "./parcellaire.js";

// ════════════════════════════════════════════════════════════════════════════
// Widget 1 — Ventes du mois (Sparkline)
// ════════════════════════════════════════════════════════════════════════════

/** Input `dashboard.ventesMois` — mois courant du tenant (aucun paramètre). */
export const dashboardVentesMoisInput = z.void();

/**
 * Sortie `dashboard.ventesMois`. `serie` = montants HT (en centimes) JOUR PAR JOUR du
 * mois en cours, prêts pour le composant `Sparkline` (`@champ-erp/ui`). Un tableau vide
 * = aucune vente encore ce mois-ci.
 */
export const ventesMoisSortieSchema = z.object({
  /** Chiffre d'affaires HT cumulé du mois en cours, en centimes. */
  total_ht_cents: z.number().int().nonnegative(),
  /** Nombre de ventes (factures + ventes caveau) du mois. */
  nombre_ventes: z.number().int().nonnegative(),
  /** Série journalière du CA HT (centimes/jour) pour la Sparkline. */
  serie: z.array(z.number().int().nonnegative()),
});
export type VentesMoisSortie = z.infer<typeof ventesMoisSortieSchema>;

// ════════════════════════════════════════════════════════════════════════════
// Widget 2 — Stock parcellaire par cépage (répartition de l'encépagement)
// ════════════════════════════════════════════════════════════════════════════

/** Input `dashboard.stockParcellaire` — vignoble entier du tenant (aucun paramètre). */
export const dashboardStockParcellaireInput = z.void();

/** Une ligne de répartition : la surface EN PRODUCTION portée par un cépage. */
export const stockCepageSchema = z.object({
  /** Nom du cépage (Chardonnay, Pinot Noir, Meunier…). */
  cepage: z.string(),
  /** Surface EN PRODUCTION plantée en ce cépage, en hectares (exclut jeune vigne / arrachée). */
  surface_ha: z.number().nonnegative(),
  /** Nombre de parcelles (ou sous-parcelles) concernées. */
  nombre_parcelles: z.number().int().nonnegative(),
  /** Part de ce cépage dans l'encépagement total (0 à 100 %). */
  part_pourcentage: z.number().min(0).max(100),
});
export type StockCepage = z.infer<typeof stockCepageSchema>;

/** Sortie `dashboard.stockParcellaire` — répartition de l'encépagement + total. */
export const stockParcellaireSortieSchema = z.object({
  cepages: z.array(stockCepageSchema),
  /** Surface totale EN PRODUCTION du vignoble, en hectares. */
  surface_totale_ha: z.number().nonnegative(),
});
export type StockParcellaireSortie = z.infer<typeof stockParcellaireSortieSchema>;

// ════════════════════════════════════════════════════════════════════════════
// Widget 3 — Activité récente (5 dernières opérations)
// ════════════════════════════════════════════════════════════════════════════

/** Nombre d'opérations affichées par défaut dans le widget « activité récente ». */
export const ACTIVITE_RECENTE_LIMITE_DEFAUT = 5;
/** Borne haute du nombre d'opérations demandables (defense in depth). */
export const ACTIVITE_RECENTE_LIMITE_MAX = 20;

/** Input `dashboard.activiteRecente` — limite optionnelle (défaut 5). */
export const dashboardActiviteRecenteInput = z
  .object({
    limite: z.number().int().min(1).max(ACTIVITE_RECENTE_LIMITE_MAX).optional(),
  })
  .optional();
export type DashboardActiviteRecenteInput = z.infer<typeof dashboardActiviteRecenteInput>;

/** Une opération récente issue de l'`audit_log` (5 dernières du tenant). */
export const activiteRecenteItemSchema = z.object({
  /** Identifiant de l'entrée d'audit. */
  id: z.string(),
  /** Code de l'action (ex. `tirage.create`, `vente.facture.emettre`). */
  action: z.string(),
  /** Libellé humain de l'opération (« Tirage du lot L-2025-012 »…). */
  libelle: z.string(),
  /** Date/heure de l'opération. */
  date: z.date(),
  /** Nom lisible de l'auteur (dénormalisé), null si inconnu/retiré. */
  auteur_nom: z.string().nullable(),
  /** Route RELATIVE de l'ERP vers l'entité concernée (navigation directe), null si aucune. */
  url_cible: z.string().nullable(),
});
export type ActiviteRecenteItem = z.infer<typeof activiteRecenteItemSchema>;

/** Sortie `dashboard.activiteRecente` — liste ordonnée (plus récent en premier). */
export const activiteRecenteSortieSchema = z.array(activiteRecenteItemSchema);
export type ActiviteRecenteSortie = z.infer<typeof activiteRecenteSortieSchema>;

// ════════════════════════════════════════════════════════════════════════════
// Widget 4 — Rendement par parcelle (3 zones, jamais hardcodé)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Zones du widget rendement — miroir EXACT des libellés métier du cadrage § 1. Une
 * seule règle vraie : « dépasser le disponible ≠ non conforme » (l'excédent va en
 * réserve tant qu'on reste sous le BUTOIR). La non-conformité = dépassement du butoir.
 *   - `dans_le_disponible` : réel ≤ disponible commercialisable.
 *   - `excedent_reserve`   : disponible < réel ≤ butoir (excédent à loger en réserve).
 *   - `hors_aoc`           : réel > butoir (excédent hors AOC / distillation).
 *   - `non_renseigne`      : disponible de l'année non publié → ni jauge ni couleur.
 */
export const RENDEMENT_ZONES = [
  "dans_le_disponible",
  "excedent_reserve",
  "hors_aoc",
  "non_renseigne",
] as const;

/** Zone de conformité d'un rendement (indicative à la parcelle). */
export const rendementZoneSchema = z.enum(RENDEMENT_ZONES);
export type RendementZone = z.infer<typeof rendementZoneSchema>;

/** Libellés FR d'affichage des zones (repris MOT POUR MOT du cadrage métier § 1). */
export const RENDEMENT_ZONE_LABELS: Record<RendementZone, string> = {
  dans_le_disponible: "Dans le disponible",
  excedent_reserve: "Au-dessus du disponible — excédent à mettre en réserve",
  hors_aoc: "Au-dessus du butoir — excédent hors AOC (distillation)",
  non_renseigne: "Rendement de l'année non renseigné",
};

/** Input `dashboard.rendementParcelle` — campagne ciblée (défaut : campagne active). */
export const dashboardRendementInput = z.object({ campagne_id: idSchema.optional() }).optional();
export type DashboardRendementInput = z.infer<typeof dashboardRendementInput>;

/**
 * Rendement d'UNE parcelle sur la campagne. Le rendement réel est `null` quand la
 * surface EN PRODUCTION est nulle (jeune vigne / arrachée) → pas de division.
 */
export const rendementParcelleSchema = z.object({
  parcelle_id: idSchema,
  /** Libellé de la parcelle (lieu-dit / nom). */
  parcelle_libelle: z.string(),
  /** Cépage dominant de la parcelle (affichage), null si non déterminé. */
  cepage: z.string().nullable(),
  /** Surface EN PRODUCTION de la parcelle, en hectares. */
  surface_production_ha: z.number().nonnegative(),
  /** Rendement réel = Σ pesées (kg) ÷ surface en production (ha) ; null si surface = 0. */
  rendement_reel_kg_ha: z.number().nonnegative().nullable(),
  /** Zone de conformité (indicative — la conformité s'apprécie à l'exploitation). */
  zone: rendementZoneSchema,
});
export type RendementParcelle = z.infer<typeof rendementParcelleSchema>;

/**
 * Sortie `dashboard.rendementParcelle`. Les seuils `disponible` / `butoir` sont
 * CAMPAGNE-level (lus depuis `campagnes`, jamais hardcodés) : `disponible` = null →
 * année non publiée (zones `non_renseigne`) ; `butoir` = null → widget à 2 zones.
 * La conformité RÉELLE s'apprécie sur `total_exploitation` (parcelle = indicatif).
 */
export const rendementParcelleGlobalSortieSchema = z.object({
  campagne_id: idSchema,
  /** Millésime de la campagne. */
  millesime: z.number().int(),
  /** Rendement disponible commercialisable de l'année (kg/ha) — `campagnes.rendement_max_kg_ha`. */
  rendement_disponible_kg_ha: z.number().nonnegative().nullable(),
  /** Rendement butoir AOC de l'année (kg/ha) — `campagnes.rendement_butoir_kg_ha`. */
  rendement_butoir_kg_ha: z.number().nonnegative().nullable(),
  /** Détail par parcelle (indicatif). */
  parcelles: z.array(rendementParcelleSchema),
  /** Agrégat au niveau EXPLOITATION — c'est ici que la conformité rendement s'apprécie. */
  total_exploitation: z.object({
    surface_production_ha: z.number().nonnegative(),
    rendement_reel_kg_ha: z.number().nonnegative().nullable(),
    zone: rendementZoneSchema,
  }),
});
export type RendementParcelleGlobalSortie = z.infer<typeof rendementParcelleGlobalSortieSchema>;

// ════════════════════════════════════════════════════════════════════════════
// Widget 5 — Performance commerciale par cuvée (JAMAIS de marge)
// ════════════════════════════════════════════════════════════════════════════

/** Input `dashboard.performanceCuvees` — millésime commercial optionnel (défaut : 12 derniers mois). */
export const dashboardPerformanceCuveesInput = z
  .object({ annee: z.number().int().min(2000).max(2100).optional() })
  .optional();
export type DashboardPerformanceCuveesInput = z.infer<typeof dashboardPerformanceCuveesInput>;

/**
 * Performance commerciale d'UNE cuvée. VOLONTAIREMENT SANS marge : le MVP n'a aucune
 * donnée de coût de production → une « marge » serait un chiffre inventé (interdit).
 * On expose uniquement du RÉEL issu des ventes (Lot G).
 */
export const performanceCuveeSchema = z.object({
  cuvee_id: idSchema,
  /** Nom commercial de la cuvée. */
  cuvee_nom: z.string(),
  /** Style réel de la cuvée, utilisé comme repère visuel secondaire. */
  cuvee_style: cuveeStyleSchema,
  /** Chiffre d'affaires HT réalisé sur la cuvée, en centimes. */
  ca_ht_cents: z.number().int().nonnegative(),
  /** Prix de vente HT moyen par bouteille équivalent 75 cl, en centimes. */
  prix_moyen_cents: z.number().int().nonnegative(),
  /** Volume vendu, en cols (bouteilles physiques). */
  volume_cols: z.number().int().nonnegative(),
});
export type PerformanceCuvee = z.infer<typeof performanceCuveeSchema>;

/** Sortie `dashboard.performanceCuvees` — une ligne par cuvée (triée par CA décroissant). */
export const performanceCuveesSortieSchema = z.array(performanceCuveeSchema);
export type PerformanceCuveesSortie = z.infer<typeof performanceCuveesSortieSchema>;
